import React, { useState, useEffect, useCallback } from "react";
import { SERVER_URL } from "../../../constants.js";
import AddAdultStay from "../../AddsEdits/Adult/AddAdultStay.js";
import EditAdultStay from "../../AddsEdits/Adult/EditAdultStay.js";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import { Snackbar, Alert, Box, CircularProgress, Divider, Typography } from "@mui/material";

import { formatLocalDateOnly } from "../../shared utils/dateFormatters.js";

// ✅ reuse Transfers module
import AddTransferDialog from "../../Transfers/jsx/dialogs/AddTransferDialog.jsx";
import EditTransferDialog from "../../Transfers/jsx/dialogs/EditTransferDialog.jsx";
import useTransferMutations from "../../Transfers/hooks/useTransferMutations.js";
import useAdultTransfers from "../../Transfers/hooks/useAdultTransfers.js";
import { jsonOr } from "../../../utils/http";

function AdultStays(props) {
  
    const { selectedPerson } = props;

    const [stays, setStays] = useState([]);
    const [selectedStay, setSelectedStay] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [campuses, setCampuses] = useState([]);

    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastSeverity, setToastSeverity] = useState("success");

    const showToast = (message, severity = "success") => {
        setToastMessage(message);
        setToastSeverity(severity);
        setToastOpen(true);
    };
    const handleToastClose = () => setToastOpen(false);

    function toValidDate(value) {
        if (!value) return null;
        if (value instanceof Date && !isNaN(value)) return value;

        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const d = new Date(`${value}T00:00:00`); // local midnight
            return isNaN(d) ? null : d;
        }

        const d = new Date(value);
        return isNaN(d) ? null : d;
    }
    
    const adultId = selectedPerson?.id;
    const transferDate = toValidDate(selectedStay?.arrivaldate);

    const { transfers, loading: transfersLoading, refetch: refetchTransfers } =
        useAdultTransfers(adultId);

    const {
        loading: transferMutating,
        addTransfers,
        editTransfers,
        deleteTransferById,
    } = useTransferMutations();

    const [addTransferOpen, setAddTransferOpen] = useState(false);
    const [editTransferOpen, setEditTransferOpen] = useState(false);
    const [selectedTransfer, setSelectedTransfer] = useState(null);

    useEffect(() => {
        if (!adultId) return;

        const token = sessionStorage.getItem("bearer");
        fetch(`${SERVER_URL}api/campuses/active`, {
            headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => (jsonOr(res, [])))
        .then((data) => setCampuses(Array.isArray(data) ? data : []))
        .catch((err) => console.error("Failed to fetch campuses:", err));
    }, [adultId]);

    const fetchStays = useCallback(() => {
        if (!adultId) return;

        const token = sessionStorage.getItem("bearer");
        fetch(`${SERVER_URL}api/nsrs/${adultId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
            if (response.status === 204) return [];
            if (response.ok) return response.json();
            showToast("Failed to fetch stays", "error");
            return [];
        })
        .then((data) => {
            const sorted = [...(data || [])].sort((a, b) =>
                String(a.arrivaldate || "").localeCompare(String(b.arrivaldate || ""))
            );

            setStays(sorted);
            setSelectedStay((prev) => prev || (sorted.length > 0 ? sorted[0] : null));
        })
        .catch((err) => console.error(err));
    }, [adultId]);

    useEffect(() => {
        fetchStays();
    }, [fetchStays]);

    const addAdultStay = (stay) => {
        const formattedStay = {
            ...stay,
            arrivaldate: formatLocalDateOnly(stay.arrivaldate),
            departuredate: formatLocalDateOnly(stay.departuredate),
        };
        const token = sessionStorage.getItem("bearer");

        return fetch(`${SERVER_URL}api/nsrs/${adultId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formattedStay),
        })
        .then((response) => {
            if (response.ok) {
                fetchStays();
                showToast("Adult stay added successfully!");
            } else {
                showToast("Failed to add adult stay.", "error");
            }
        })
        .catch((err) => console.error(err));
    };

    const handleEditClick = (stay) => {
        setSelectedStay(stay);
        setEditOpen(true);
    };

    const editStay = (stay, stayId) => {
        // Moving campus releases the adult from the old campus's campus-linked
        // groups (activities/transport/bedrooms/classes); they need re-allocating
        // at the new one — we don't auto-enrol. Warn before saving.
        const oldCampus = selectedStay?.residentialBase ?? selectedStay?.campus;
        const newCampus = stay?.residentialBase ?? stay?.campus;
        if (oldCampus && newCampus && oldCampus !== newCampus) {
            const ok = window.confirm(
                `Move campus?\n\n` +
                `Changing this stay from ${oldCampus} to ${newCampus} will release this ` +
                `adult from ${oldCampus}'s activity, transport, bedroom and class groups. ` +
                `You'll need to re-allocate them at ${newCampus} — we don't auto-enrol.\n\n` +
                `Continue?`
            );
            if (!ok) return;
        }

        const formattedStay = {
            ...stay,
            arrivaldate: formatLocalDateOnly(stay.arrivaldate),
            departuredate: formatLocalDateOnly(stay.departuredate),
        };
        const token = sessionStorage.getItem("bearer");

        fetch(`${SERVER_URL}api/nsrs/${stayId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formattedStay),
        })
        .then((response) => {
            if (response.ok) {
                fetchStays();
                showToast("Adult stay edited successfully!");
            } else {
                showToast("Failed to edit adult stay.", "error");
            }
        })
        .catch((err) => console.error(err));
    };

    const handleDeleteClick = (stay) => {
        const campus = stay?.residentialBase ?? stay?.campus;
        // Removing a stay releases the adult from that campus's campus-linked
        // groups; if they have another stay they need re-allocating there.
        const confirmed = window.confirm(
            `Remove this stay?\n\n` +
            (campus
                ? `Deleting this ${campus} stay will release this adult from ${campus}'s ` +
                  `activity, transport, bedroom and class groups. If they have another stay, ` +
                  `re-allocate them there — we don't auto-enrol.\n\n`
                : `This adult will be released from that campus's activity, transport, bedroom ` +
                  `and class groups. If they have another stay, re-allocate them there — we ` +
                  `don't auto-enrol.\n\n`) +
            `Continue?`
        );
        if (!confirmed) return;

        const stayId = stay?.id;
        const token = sessionStorage.getItem("bearer");
        fetch(`${SERVER_URL}api/nsrs/${stayId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
            if (response.status === 204) fetchStays();
            else if (response.status === 404) alert("Stay not found, could not delete.");
            else alert("Failed to delete stay.");
        })
        .catch((err) => {
            console.error(err);
            alert("Something went wrong!");
        });
    };

    const handleAddTransferSaved = async (payload, target) => {
        const res = await addTransfers({
            transfer: payload,
            targets: [target], // { personType:"ADULT", adultId }
        });

        if (res.ok) {
            showToast("Transfer added.");
            setAddTransferOpen(false);
            await refetchTransfers();
        } else {
            showToast("Failed to add transfer.", "error");
        }
    };

    const handleEditTransferSaved = async (payload, target, tid) => {
        const transferId = Number(tid);
        const res = await editTransfers({
            transfer: payload,
            targets: [{ transferId }],
        });

        if (res.ok) {
            showToast("Transfer updated.");
            setEditTransferOpen(false);
            setSelectedTransfer(null);
            await refetchTransfers();
        } else {
            showToast("Failed to update transfer.", "error");
        }
    };

    const handleDeleteTransfer = async (transferId) => {
        const confirmed = window.confirm("Delete this transfer?");
        if (!confirmed) return;

        const res = await deleteTransferById(Number(transferId));
        if (res.ok) {
            showToast("Transfer deleted.");
            await refetchTransfers();
        } else {
            showToast(`Failed to delete transfer (${res.status}).`, "error");
        }
    };

    const sortedTransfers = [...(transfers || [])].sort(
        (a, b) => (a.transferDate || "").localeCompare(b.transferDate || "")
    );

    return (
        <React.Fragment>
        {/* ===================== STAYS ===================== */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Stays</Typography>
            <AddAdultStay adultId={adultId} addStay={addAdultStay} campuses={campuses} />
        </Box>
        <div className="detail-card payments-card" style={{ padding: "20px 0" }}>
            <table
                style={{
                width: "80%",
                textAlign: "left",
                margin: "auto",
                borderCollapse: "collapse",
                }}
            >
                <thead>
                    <tr>
                        <th>Arrival</th>
                        <th>Campus</th>
                        <th>Departure</th>
                        <th>Overnight Resident</th>
                        <th>Gender</th>
                        <th>Notes</th>
                        <th />
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {stays.map((stay) => (
                        <tr
                            key={stay.id}
                            onClick={() => setSelectedStay(stay)}
                            style={{
                                cursor: "pointer",
                                background:
                                    selectedStay?.id === stay.id
                                    ? "rgba(25, 118, 210, 0.06)"
                                    : "transparent",
                            }}
                        >
                            <td>{stay.arrivaldate}</td>
                            <td>{stay.residentialBase}</td>
                            <td>{stay.departuredate}</td>
                            <td>{stay.overnightResident ? "Yes" : "No"}</td>
                            <td>{stay.gender || "—"}</td>
                            <td>{stay.notes}</td>
                            <td>
                                <IconButton onClick={() => handleEditClick(stay)} color="primary">
                                    <EditIcon />
                                </IconButton>
                            </td>
                            <td>
                                <IconButton onClick={() => handleDeleteClick(stay)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        {editOpen && (
            <EditAdultStay
                adultStayToEdit={selectedStay}
                updateAdultStay={editStay}
                adultId={adultId}
                open={editOpen}
                onClose={() => setEditOpen(false)}
                campuses={campuses}
            />
        )}

      {/* ===================== TRANSFERS ===================== */}
    <Divider sx={{ my: 3 }} />
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Transfers</Typography>
        <Button
            startIcon={<AddCircleOutlineIcon />}
            variant="outlined"
            size="small"
            onClick={() => setAddTransferOpen(true)}
            disabled={!adultId}
        >
            Add transfer
        </Button>
    </Box>

    <Box sx={{ width: "80%", margin: "0 auto" }}>
            {(transfersLoading || transferMutating) && <CircularProgress size={18} />}

            <div className="detail-card payments-card" style={{ padding: "20px 0" }}>
                <table
                    style={{
                        width: "100%",
                        textAlign: "left",
                        margin: "auto",
                        borderCollapse: "collapse",
                    }}
                >
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Depart</th>
                            <th>Arrive</th>
                            <th>Flight</th>
                            <th>Dep Time</th>
                            <th>Arr Time</th>
                            <th>Notes</th>
                            <th />
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {sortedTransfers.map((t) => (
                            <tr key={t.id}>
                                <td>{t.transferDate || "—"}</td>
                                <td>{t.depart || ""}</td>
                                <td>{t.arrive || ""}</td>
                                <td>{t.flightId || ""}</td>
                                <td>{t.departureTime || ""}</td>
                                <td>{t.arrivalTime || ""}</td>
                                <td>{t.notes || ""}</td>
                                <td>
                                    <IconButton
                                        onClick={() => {
                                            setSelectedTransfer(t);
                                            setEditTransferOpen(true);
                                        }}
                                        color="primary"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </td>
                                <td>
                                    <IconButton onClick={() => handleDeleteTransfer(t.id)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </td>
                            </tr>
                        ))}
                        {sortedTransfers.length === 0 && (
                            <tr>
                                <td colSpan={9} style={{ padding: 12, color: "#888" }}>
                                    No transfers found for this adult.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <AddTransferDialog
                open={addTransferOpen}
                onClose={() => setAddTransferOpen(false)}
                transferDate={transferDate}
                direction={null}
                adultId={adultId}
                personType="ADULT"
                campuses={campuses}
                onSaved={handleAddTransferSaved}
            />

            <EditTransferDialog
                open={editTransferOpen}
                onClose={() => {
                    setEditTransferOpen(false);
                    setSelectedTransfer(null);
                }}
                transferDate={transferDate}
                adultId={adultId}
                personType="ADULT"
                transferId={selectedTransfer?.id}
                campuses={campuses}
                onSaved={handleEditTransferSaved}
            />
        </Box>

        <Snackbar
            open={toastOpen}
            autoHideDuration={4000}
            onClose={handleToastClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
            <Alert onClose={handleToastClose} severity={toastSeverity} sx={{ width: "100%" }}>
                {toastMessage}
            </Alert>
        </Snackbar>
    </React.Fragment>
  );
}

export default AdultStays;
